import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, BookOpen, Users, Search, CheckCircle2, Loader2, UserPlus, X, Filter
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import api from "../../lib/api";
import { cn } from "../../lib/utils";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };

const getInitials = (name) => name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";

const ManagerTeamEnrollPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedMember = searchParams.get("member");

  const [step, setStep] = useState(1); // 1=select course, 2=select members, 3=confirm
  const [courses, setCourses] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [courseSearch, setCourseSearch] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedMembers, setSelectedMembers] = useState(preselectedMember ? [preselectedMember] : []);
  const [result, setResult] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/manager/team/courses-catalog"),
      api.get("/manager/team"),
    ]).then(([coursesRes, teamRes]) => {
      setCourses(coursesRes.data || []);
      setMembers(teamRes.data?.members || []);
      if (preselectedMember) setStep(1);
    }).catch(console.error).finally(() => setLoading(false));
  }, [preselectedMember]);

  const categories = [...new Set(courses.map(c => c.category).filter(Boolean))];

  const filteredCourses = courses.filter(c => {
    const matchSearch = !courseSearch || c.title.toLowerCase().includes(courseSearch.toLowerCase());
    const matchCat = categoryFilter === "all" || c.category === categoryFilter;
    return matchSearch && matchCat;
  });

  const filteredMembers = members.filter(m => 
    !memberSearch || m.full_name.toLowerCase().includes(memberSearch.toLowerCase()) || m.email.toLowerCase().includes(memberSearch.toLowerCase())
  );

  const toggleMember = (id) => {
    setSelectedMembers(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const selectAllMembers = () => {
    if (selectedMembers.length === filteredMembers.length) setSelectedMembers([]);
    else setSelectedMembers(filteredMembers.map(m => m.id));
  };

  const handleEnroll = async () => {
    if (!selectedCourse || selectedMembers.length === 0) return;
    setEnrolling(true);
    try {
      const res = await api.post("/manager/team/enroll", {
        course_id: selectedCourse.id,
        user_ids: selectedMembers,
      });
      setResult(res.data);
      setStep(3);
      toast.success(res.data.message || "Enrollment successful");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrollment failed");
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) return (
    <AdminLayout title="Enroll Team" variant="manager">
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="Enroll Team Members" variant="manager">
      <motion.div className="space-y-6 max-w-4xl" variants={container} initial="hidden" animate="show" data-testid="team-enroll-page">
        {/* Header */}
        <motion.div variants={item}>
          <Button variant="ghost" size="sm" className="gap-1.5 mb-3 -ml-2" onClick={() => navigate("/manager/team")} data-testid="back-to-team">
            <ArrowLeft className="w-4 h-4" /> Back to Team
          </Button>
          <h1 className="text-2xl font-extrabold tracking-tight">Enroll Team Members</h1>
          <p className="text-sm text-muted-foreground mt-1">Select a course and team members to enroll</p>
        </motion.div>

        {/* Steps Indicator */}
        <motion.div variants={item} className="flex items-center gap-2">
          {["Select Course", "Select Members", "Confirmation"].map((label, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors",
                step > i + 1 ? "bg-emerald-500 text-white" : step === i + 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              )}>
                {step > i + 1 ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              <span className={cn("text-sm hidden sm:inline", step === i + 1 ? "font-semibold" : "text-muted-foreground")}>{label}</span>
              {i < 2 && <div className="w-8 h-px bg-border" />}
            </div>
          ))}
        </motion.div>

        {/* Step 1: Select Course */}
        {step === 1 && (
          <motion.div variants={item} className="space-y-4">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search courses..." value={courseSearch} onChange={e => setCourseSearch(e.target.value)} className="pl-9" data-testid="course-search" />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]"><Filter className="w-3.5 h-3.5 mr-2" /><SelectValue placeholder="Category" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2 max-h-[400px] overflow-y-auto pr-1">
              {filteredCourses.length === 0 ? (
                <Card className="border"><CardContent className="p-6 text-center text-muted-foreground">No courses available</CardContent></Card>
              ) : filteredCourses.map(c => (
                <Card
                  key={c.id}
                  className={cn("border cursor-pointer transition-all hover:shadow-sm", selectedCourse?.id === c.id && "ring-2 ring-primary border-primary")}
                  onClick={() => setSelectedCourse(c)}
                  data-testid={`course-option-${c.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <BookOpen className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{c.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {c.category && <Badge variant="outline" className="text-[10px]">{c.category}</Badge>}
                          {c.is_compliance && <Badge variant="destructive" className="text-[10px]">Compliance</Badge>}
                          <span className="text-[10px] text-muted-foreground">{c.difficulty_level}</span>
                        </div>
                      </div>
                      {selectedCourse?.id === c.id && <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-end">
              <Button disabled={!selectedCourse} onClick={() => setStep(2)} data-testid="next-to-members">
                Next: Select Members
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Select Members */}
        {step === 2 && (
          <motion.div variants={item} className="space-y-4">
            <Card className="border bg-muted/30">
              <CardContent className="p-3 flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-primary" />
                <div>
                  <p className="text-sm font-semibold">{selectedCourse?.title}</p>
                  <p className="text-[10px] text-muted-foreground">{selectedCourse?.category} &middot; {selectedCourse?.difficulty_level}</p>
                </div>
                <Button variant="ghost" size="sm" className="ml-auto text-xs" onClick={() => setStep(1)}>Change</Button>
              </CardContent>
            </Card>

            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Search members..." value={memberSearch} onChange={e => setMemberSearch(e.target.value)} className="pl-9" data-testid="member-search" />
              </div>
              <Button variant="outline" size="sm" onClick={selectAllMembers} data-testid="select-all-members">
                {selectedMembers.length === filteredMembers.length ? "Deselect All" : "Select All"}
              </Button>
            </div>

            <p className="text-sm text-muted-foreground">{selectedMembers.length} of {members.length} selected</p>

            <div className="grid gap-2 max-h-[350px] overflow-y-auto pr-1">
              {filteredMembers.map(m => (
                <Card
                  key={m.id}
                  className={cn("border cursor-pointer transition-all hover:shadow-sm", selectedMembers.includes(m.id) && "ring-2 ring-primary border-primary")}
                  onClick={() => toggleMember(m.id)}
                  data-testid={`member-option-${m.id}`}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-3">
                      <Checkbox checked={selectedMembers.includes(m.id)} className="shrink-0" />
                      <Avatar className="h-9 w-9">
                        <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">{getInitials(m.full_name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{m.full_name}</p>
                        <p className="text-xs text-muted-foreground truncate">{m.department || m.email}</p>
                      </div>
                      <div className="text-right hidden sm:block">
                        <p className="text-xs font-medium">{m.total_courses} courses</p>
                        <p className="text-[10px] text-muted-foreground">{m.completion_rate}% complete</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="back-to-courses">Back</Button>
              <Button disabled={selectedMembers.length === 0 || enrolling} onClick={handleEnroll} className="gap-2" data-testid="confirm-enroll-btn">
                {enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Enroll {selectedMembers.length} Member{selectedMembers.length !== 1 ? "s" : ""}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && result && (
          <motion.div variants={item} className="space-y-4">
            <Card className="border">
              <CardContent className="p-8 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Enrollment Complete</h2>
                  <p className="text-muted-foreground mt-1">{result.message}</p>
                </div>
              </CardContent>
            </Card>

            {result.enrolled?.length > 0 && (
              <Card className="border">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Successfully Enrolled ({result.enrolled.length})</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  {result.enrolled.map((e, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm py-1">
                      <Avatar className="h-6 w-6"><AvatarFallback className="text-[9px] bg-emerald-50 text-emerald-700">{getInitials(e.name)}</AvatarFallback></Avatar>
                      <span>{e.name}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {result.skipped?.length > 0 && (
              <Card className="border">
                <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-amber-600"><X className="w-4 h-4" /> Skipped ({result.skipped.length})</CardTitle></CardHeader>
                <CardContent className="space-y-1">
                  {result.skipped.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm py-1 text-muted-foreground">
                      <Avatar className="h-6 w-6"><AvatarFallback className="text-[9px] bg-amber-50 text-amber-700">{getInitials(s.name)}</AvatarFallback></Avatar>
                      <span>{s.name} — {s.reason}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate("/manager/team")} data-testid="back-to-roster">Back to Team</Button>
              <Button onClick={() => { setStep(1); setSelectedCourse(null); setSelectedMembers([]); setResult(null); }} data-testid="enroll-more">
                Enroll More
              </Button>
            </div>
          </motion.div>
        )}
      </motion.div>
    </AdminLayout>
  );
};

export default ManagerTeamEnrollPage;
